import parse from 'date-fns/parse';
import format from 'date-fns/format';
import subYears from 'date-fns/sub_years';
import addYears from 'date-fns/add_years';
// Fetch
import StateFetch from '../fetch/StateFetch';

export default class Helper {
  /**
   * Given a dollar amount will abbreviate it into Thousands, Millions, or Billions as appropriate
   * @param value
   * @return {*}
   */
  static abbreviateCurrency(value) {
    let num = Math.floor(value);
    if (num >= 1000000000) return this.displayCurrency(num / 1000000000, 1) + 'B';
    if (num >= 1000000) return this.displayCurrency(num / 1000000, 1) + 'M';
    if (num >= 1000) return this.displayCurrency(num / 1000, 1) + 'k';
    return this.displayCurrency(num, 0);
  }

  /**
   * Given a number will abbreviate it into Thousands, Millions, or Billions as appropriate
   * @param value
   * @return {*}
   */
  static abbreviateNumber(value) {
    let num = Math.floor(value);
    if (num >= 1000000000) return this.displayInteger(num / 1000000000) + 'B';
    if (num >= 1000000) return this.displayInteger(num / 1000000) + 'M';
    if (num >= 1000) return this.displayInteger(num / 1000) + 'k';
    return this.displayInteger(num);
  }

  /**
   * Given a number will abbreviate it into Thousands, Millions, or Billions as appropriate (DECIMAL)
   * @param value
   * @return {*}
   */
  static abbreviateDecimal(value) {
    let num = value;
    if (num >= 1000000000) return this.displayDecimal(num / 1000000000, 1) + 'B';
    if (num >= 1000000) return this.displayDecimal(num / 1000000, 1) + 'M';
    if (num >= 1000) return this.displayDecimal(num / 1000, 1) + 'k';
    return this.displayDecimal(num, 0);
  }

  /**
   * Calls a given function after a given period of time to implement a debouncing algorithm which avoids unnecessary
   * repeated calls to the function over a short time period.
   *
   * @param func: function: The function to call after the time period elapses.
   * @param debounceTime: int: The maximum frequency at which the function should be executed in milliseconds.
   */
  static debounce = (func, debounceTime) => {
    // Allocate an object to track the timer identifier for each function.
    if (!func.debounceTimers) func.debounceTimers = {};

    // If there's already a timer for this function, clear it. clearTimeout handles undefined arguments.
    clearTimeout(func.debounceTimers);

    // Start a new timer for this function.
    func.debounceTimers = setTimeout(func, debounceTime);
  };

  static displayPhoneNumber = (phoneNumber) => {
    if (!phoneNumber || phoneNumber === '' || phoneNumber.length < 10) {
      return '';
    } else if (phoneNumber.length === 10) {
      //handles format like 1111111111
      return (
        '(' +
        phoneNumber.slice(0, 3) +
        ') ' +
        phoneNumber.slice(3, 6) +
        '-' +
        phoneNumber.slice(6, 10)
      );
    } else if (phoneNumber[0] === '+') {
      ///handles formats like 11111111111
      return (
        phoneNumber.slice(0, 2) +
        ' (' +
        phoneNumber.slice(2, 5) +
        ') ' +
        phoneNumber.slice(5, 8) +
        '-' +
        phoneNumber.slice(8, 12)
      );
    } else {
      //handles formats like +1111111111
      return (
        '+' +
        phoneNumber.slice(0, 1) +
        ' (' +
        phoneNumber.slice(1, 4) +
        ') ' +
        phoneNumber.slice(4, 7) +
        '-' +
        phoneNumber.slice(7, 11)
      );
    }
  };

  static displayCurrency = (value, places = 2) => {
    value = parseFloat(value);
    if (isNaN(value)) {
      return '';
    }

    // Note to maintainers - this will save you some time:
    // loLocalString() is tempting, but it fails in IE11 so don't do it
    // seems like regex is the only reliable way to do this in all browsers
    // Second note to maintainers- Intl.NumberFormat also seems like a cleaner
    // solution, however at the time of writing it fails in Safari on a mobile device (iPd)

    return '$' + value.toFixed(places).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  static displayDecimal = (value, places = 2) => {
    value = parseFloat(value);
    if (isNaN(value)) {
      return '';
    }

    // Note to maintainers - this will save you some time:
    // toLocaleString() is tempting, but it fails in IE11 so don't do it
    // seems like regex is the only reliable way to do this in all browsers

    return value.toFixed(places).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  /**
   * Round a float number to the float number with the number (places) after dot .
   * roundFloat(-12.2222, 2) => -12.22
   * roundFloat(-12.6666, 2) => -12.67
   * @param {*} value
   * @param {*} places
   */
  static roundFloat = (value, places = 2) => {
    if (!value) {
      return 0;
    }
    return Math.round(value * Math.pow(10, places)) / Math.pow(10, places);
  };

  static displayInteger = (value) => {
    value = parseInt(value, 10);
    if (isNaN(value)) {
      return '';
    }
    return value.toString().replace(/(.)(?=(\d{3})+$)/g, '$1,');
  };

  static displayDate = (date, fromFormat) => {
    if (date === null) {
      return '';
    }
    //And fromFormat to prevent the following error/warning:
    //Deprecation warning: value provided is not in a recognized ISO format.
    //moment construction falls back to js Date(), which is not reliable across all browsers and versions.
    //Non ISO date formats are discouraged and will be removed in an upcoming major release.
    var d = null;
    if (fromFormat) {
      d = parse(date, fromFormat);
    } else {
      d = parse(date);
    }
    d = format(d, 'M/D/YYYY');
    return d;
  };

  static displayDateMMDDYYYY = (date, fromFormat) => {
    if (date === null) {
      return '';
    }
    //And fromFormat to prevent the following error/warning:
    //Deprecation warning: value provided is not in a recognized ISO format.
    //moment construction falls back to js Date(), which is not reliable across all browsers and versions.
    //Non ISO date formats are discouraged and will be removed in an upcoming major release.
    var d = null;
    if (fromFormat) {
      d = parse(date, fromFormat);
    } else {
      d = parse(date);
    }
    d = format(d, 'MM/DD/YYYY');
    return d;
  };

  static displayDateYYYYMMDD = (date, fromFormat) => {
    if (date === null) {
      return '';
    }
    //And fromFormat to prevent the following error/warning:
    //Deprecation warning: value provided is not in a recognized ISO format.
    //moment construction falls back to js Date(), which is not reliable across all browsers and versions.
    //Non ISO date formats are discouraged and will be removed in an upcoming major release.
    var d = null;
    if (fromFormat) {
      d = parse(date, fromFormat);
    } else {
      d = parse(date);
    }
    d = format(d, 'YYYY/MM/DD');
    return d;
  };

  static displayDayMonth = (date) => {
    if (date === null) {
      return '';
    }
    return format(date, 'Do MMMM');
  };

  /**
   * yearAmount: the amount to increase(+) or decrease(-) the year of date before displaying the year
   */
  static displayYear = (date, yearAmount) => {
    if (date === null) {
      return '';
    }
    if (yearAmount) {
      if (yearAmount < 0) {
        //subtract the year of date
        date = subYears(date, yearAmount * -1);
      }
      if (yearAmount > 0) {
        //add the year of date
        date = addYears(date, yearAmount);
      }
    }

    return format(date, 'YYYY');
  };

  static displayDateTime = (date, fromFormat) => {
    if (date === null) {
      return '';
    }
    //And fromFormat to prevent the following error/warning:
    //Deprecation warning: value provided is not in a recognized ISO format.
    //moment construction falls back to js Date(), which is not reliable across all browsers and versions.
    //Non ISO date formats are discouraged and will be removed in an upcoming major release.
    var d = null;
    if (fromFormat) {
      d = format(date, fromFormat);
    } else {
      d = format(date, 'MM/DD/YYYY hh:mm:ss');
    }
    return d;
  };

  static displayBool = (val) => {
    if (val) {
      return 'Yes';
    } else {
      return 'No';
    }
  };

  /**
   * Returns a searchable google string for a given entity
   * @param entity. Entity object
   * @returns {*} String to be sent to Google
   */
  static googleStringFromEntity(entity) {
    if (!entity) {
      return null;
    }

    var googleString = entity.entity_name;
    if (entity.city && entity.state) {
      googleString += ' ' + entity.city + ' ' + entity.state;
    }
    return googleString;
  }

  /**
   * Returns a searchable google string for a given entity and contact
   * @param entity. Entity object
   * @param contact. Contact object
   * @returns {*} String to be sent to Google
   */
  static googleStringFromEntityAndContact(entity, contact) {
    if (!entity || !contact) {
      return null;
    }

    var googleString = contact.name + ' ' + this.googleStringFromEntity(entity);
    return googleString;
  }

  /**
   * Checks for an empty ({}) object
   */
  static isEmpty(obj) {
    for (var key in obj) {
      if (obj.hasOwnProperty(key)) return false;
    }
    return true;
  }

  /**
   * Returns true if a string is a JSON object, false if not
   * @param str: The string value to test
   * @returns {boolean} true if json, false otherwise
   */
  static isJSONStringObject(str) {
    try {
      if (typeof JSON.parse(str) !== 'object') {
        throw new Error('String needs to be an object');
      }
    } catch (e) {
      return false;
    }
    return true;
  }

  /**
   * Returns true if a string is a boolean type when parsed, false if not
   * @param str: The string value to test
   * @returns {boolean} true if bool, false otherwise
   */
  static isJSONStringBool(str) {
    try {
      if (typeof JSON.parse(str) !== 'boolean') {
        throw new Error('String needs to be an object');
      }
    } catch (e) {
      return false;
    }
    return true;
  }

  /**
   * Compares two objects and returns true if they have the same structure and values.
   * see https://medium.com/@abc810221/object-comparison-equality-e846684114b6#.uuuk0ev0i
   *
   * @param obj1
   * @param obj2
   * @returns {boolean}
   */

  static objectComparison(obj1, obj2) {
    //check if the two actually have the same value
    if (obj1 === obj2) {
      return true;
    }

    // Since we know the objects aren't strictly equal, if either is null then they can't be equal.
    if (obj1 === null || obj2 === null) {
      return false;
    }

    // Since we know the objects aren't strictly equal, if either is undefined then they can't be equal.
    if (obj1 === undefined || obj2 === undefined) {
      return false;
    }

    //check if both objects are actually 'object' in Javascript
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
      return false;
    }
    //check if objects have the same number of keys
    if (Object.keys(obj1).length !== Object.keys(obj2).length) {
      return false;
    }
    //loop through each key and compare corresponding value
    for (var key in obj1) {
      //use recursing to check for nested object
      if (!this.objectComparison(obj2[key], obj1[key])) {
        return false;
      }
    }
    return true;
  }

  /**
   * Parses a query object from a URL, converting properly coded strings to JSON objects
   * @param paramObject: The object to parse
   * @returns {object}: The fully parsed object
   */
  static parseParametersFromURL(paramString) {
    var parsedSearchCriteria = {};
    Object.keys(paramString).forEach((k) => {
      if (Helper.isJSONStringObject(paramString[k])) {
        parsedSearchCriteria[k] = JSON.parse(paramString[k]);
      } else if (Helper.isJSONStringBool(paramString[k])) {
        parsedSearchCriteria[k] = JSON.parse(paramString[k]);
      } else {
        parsedSearchCriteria[k] = paramString[k];
      }
    });
    return parsedSearchCriteria;
  }

  /**
   * Parses a URL query string (str) into a collection of key and value pairs.
   * @param string: The URL query string to parse
   * @returns {object}: The fully parsed object
   */
  static queryStringParse(string) {
    if (!string || string === '' || string.indexOf('=') === -1) {
      return {};
    }

    let parsed = {};

    string = string.substring(string.indexOf('?') + 1);
    let p1 = string.split('&');

    p1.map((value) => {
      let params = value.split('=');
      parsed[params[0]] = params[1];
      return parsed;
    });

    return parsed;
  }

  /**
   * Given a number will abbreviate it into "sq. ft." or "acres" as appropriate
   * @param value
   * @return {*}
   */
  static abbreviateAcres(value) {
    let ret = '';
    if (value < 43560) {
      ret = Helper.abbreviateNumber(value) + ' sq. ft.';
    } else {
      ret = Helper.abbreviateDecimal(value / 43560) + ' acres';
    }
    return ret;
  }

  /**
   * Check state whether is a small state
   */
  static isASmallState(state) {
    let ret = false;
    const smallStates = [
      'AK',
      'HI',
      'ID',
      'NV',
      'MT',
      'WY',
      'UT',
      'NM',
      'ND',
      'SD',
      'NE',
      'OK',
      'AR',
      'MS',
      'WV',
      'VT',
      'NH',
      'ME',
      'RI',
      'DE',
      'DC',
    ];
    if (state && smallStates.indexOf(state) !== -1) {
      ret = true;
    }
    return ret;
  }

  /**
   * Get State name from state code
   * @param {*} stateCode
   * @return string (state name)
   */
  static getStateName = (stateCode) => {
    if (!stateCode || stateCode === '') {
      return null;
    }
    const state = StateFetch.fetchStates(stateCode);
    if (state === null || state === undefined) {
      return null;
    }

    return state.name;
  };

  /**
   * Get Sentry User Context
   * @param {object} userInfo
   * @return {object}
   */
  static getSentryUserContext(userInfo) {
    if (!userInfo || this.isEmpty(userInfo)) {
      return {};
    }
    return {
      id: userInfo.id,
      account_id: userInfo.account_id,
      account_type: userInfo.account_type,
      session_id: userInfo.session_id,
      products: userInfo.products,
      type: userInfo.type,
      role: userInfo.role,
    };
  }

  static formatPhoneAms = (phoneNumber) => {
    if (!phoneNumber || phoneNumber === '' || phoneNumber.length < 10) {
      return '';
    } else if (phoneNumber.length === 10) {
      //handles format like 1111111111
      return (
        '(' +
        phoneNumber.slice(0, 3) +
        ') ' +
        phoneNumber.slice(3, 6) +
        '-' +
        phoneNumber.slice(6, 10)
      );
    } else if (phoneNumber[0] === '+') {
      ///handles formats like 1 (111) 111-1111
      return (
        phoneNumber.slice(1, 2) +
        ' (' +
        phoneNumber.slice(2, 5) +
        ') ' +
        phoneNumber.slice(5, 8) +
        '-' +
        phoneNumber.slice(8, 12)
      );
    } else {
      //handles formats like +1 111 111 1111
      return (
        '+' +
        phoneNumber.slice(0, 1) +
        ' ' +
        phoneNumber.slice(1, 4) +
        ' ' +
        phoneNumber.slice(4, 7) +
        ' ' +
        phoneNumber.slice(7, 12)
      );
    }
  };
}

/**
 * Given an array of objects and a property name will return a filtered list
 * of objects that have unique values for the given property name
 *
 * @param originalArray
 * @param prop
 * @return {Array}
 */
export const filterArrayOfObjectsByProperty = (originalArray, prop) => {
  var newArray = [];
  var lookupObject = {};

  for (var i in originalArray) {
    if (originalArray[i]) {
      lookupObject[originalArray[i][prop]] = originalArray[i];
    }
  }

  for (i in lookupObject) {
    if (lookupObject[i]) {
      newArray.push(lookupObject[i]);
    }
  }
  return newArray;
};

export const groupBy = (objectArray, property) => {
  if (objectArray === null) {
    return [];
  }
  return objectArray.reduce(function (acc, obj) {
    var key = obj[property] === null ? '' : obj[property];
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(obj);
    return acc;
  }, {});
};

/**
 * returns an array from a space or comma separated string of items.
 * "M001 M002 M003" returns ['M001', 'M002', 'M003]
 * "M001,M002,M003" returns ['M001', 'M002', 'M003]
 *
 * @param inputString
 * @param isCsv: boolean: inputString is a comma separated list of values
 * @return array
 */
export const stringToArray = (inputString, isCsv) => {
  if (!inputString) return [];

  // handle a csv input
  if (isCsv) {
    return inputString
      .trim()
      .split(',')
      .map((e) => e.trim())
      .filter(function (e) {
        return e;
      });
  }

  // in all other cases, return an array with each "word" as an element
  return inputString.replace(/\s+/g, ' ').trim().split(/[ ,]+/);
};

/**
 * Given an array of objects (items) and a property name (prop) will return
 * the sum of the values of prop for all objects in the array
 * @param {*} items
 * @param {*} prop
 * @return number
 */
export const sumObjectArray = (items, prop) => {
  if (items === null) {
    return 0;
  }
  return items.reduce(function (a, b) {
    return b[prop] == null ? a : a + b[prop];
  }, 0);
};

export const formatInteger = (value) => {
  value = parseInt(value, 10);
  if (isNaN(value)) {
    return '';
  }
  return value.toString().replace(/(.)(?=(\d{3})+$)/g, '$1,');
};

export const isStringFalsyOrEmpty = (str) => {
  return !str || str === '' || str.trim() === '';
};
